import { useCallback, useRef, useState } from 'react';
import { useAdminReadOnly } from '../lib/admin-read-only';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postCsv, ApiError } from '../api/client';
import { apiUrl } from '../api/client';

/**
 * Same prefix the api client uses (see api/client.ts). The template download is
 * a plain anchor rather than a fetch so the browser handles the file save;
 * being same-origin, it still carries the session cookie.
 */


/**
 * Bulk CSV import for the six threat-catalog reference tables.
 *
 * One instance per admin sub-tab (adversarial sources, non-adversarial
 * sources, events, vulnerabilities, predisposing conditions, impacts).
 *
 * The wording here is deliberately non-technical. The API speaks in
 * preflight / append / upsert; the screen speaks in "check the file",
 * "add everything as new" and "update entries that already exist". Nothing
 * on screen says dry run, upsert, or legacy_code without explaining it.
 *
 * Compositions are not importable — they join five entities and need their own
 * resolution pass — so no instance of this panel is rendered on that sub-tab.
 */

export type CatalogImportEntity =
  | 'sources/adversarial'
  | 'sources/non-adversarial'
  | 'events'
  | 'vulnerabilities'
  | 'predisposing-conditions'
  | 'impacts';

/** What the API calls the mode. Never shown to the user. */
type ApiMode = 'preflight' | 'append' | 'upsert';

interface RowResult {
  row_num: number;
  status: 'created' | 'updated' | 'skipped' | 'failed' | 'valid';
  legacy_code: string | null;
  name: string | null;
  id: string | null;
  errors?: string[];
}

interface ImportSummary {
  entity: string;
  table: string;
  mode: ApiMode;
  total_rows: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  parse_errors: { row_num: number; message: string }[];
  ignored_columns: string[];
  results: RowResult[];
}

/**
 * The trigger, exported separately so it can sit beside the "+ New …" button in
 * a table's header CTA while the panel itself renders above the table. Styled
 * to match NewButton so the pair lines up.
 */
export function ImportCsvButton({ onClick, active }: { onClick: () => void; active?: boolean }) {
  if (useAdminReadOnly()) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-sm px-3 py-1 border rounded-sm ${
        active
          ? 'border-accent text-accent bg-accent/5'
          : 'border-ink-300 text-ink-700 hover:bg-ink-50'
      }`}
    >
      Import CSV
    </button>
  );
}

function StepHeading({ n, children, done }: { n: number; children: React.ReactNode; done?: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-medium ${
          done ? 'bg-accent text-white' : 'bg-ink-100 text-ink-700'
        }`}
      >
        {done ? '✓' : n}
      </span>
      <span className="text-xs font-medium text-ink-900">{children}</span>
    </div>
  );
}

export function CatalogCsvImport({
  entity,
  label,
  queryKey,
  open: openProp,
  onOpenChange,
}: {
  entity: CatalogImportEntity;
  label: string;
  /** react-query key to invalidate once rows are written. */
  queryKey: unknown[];
  /**
   * Controlled visibility. When supplied, this component renders ONLY the
   * panel — the caller owns the trigger (see ImportCsvButton) and can place it
   * wherever it belongs, e.g. next to "+ New …" in the table header. Omit both
   * props to get the self-contained button + panel.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (v: boolean) => (controlled ? onOpenChange?.(v) : setOpenState(v));

  const clearFile = useCallback(() => {
    setFileName(null);
    setCsvText(null);
    setSummary(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const onPick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSummary(null);
    setError(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText((ev.target?.result as string) ?? '');
    reader.onerror = () => setError('That file could not be read. Try saving it again as CSV.');
    reader.readAsText(file, 'utf-8');
  }, []);

  const importM = useMutation({
    mutationFn: async (mode: ApiMode) => {
      if (!csvText || csvText.trim() === '') throw new Error('Choose a CSV file first.');
      // postCsv unwraps the { data: … } envelope itself (see api/client.ts),
      // so this resolves to the summary directly. Typing it as { data: … } and
      // reading .data is what previously left the panel blank after a
      // successful import.
      return postCsv<ImportSummary>(
        `/admin/threat-catalog/${entity}/import?mode=${mode}`,
        csvText,
      );
    },
    onSuccess: (res, mode) => {
      setSummary(res);
      setError(null);
      // The panel can be taller than the viewport; without this the outcome
      // renders below the fold and the import looks like it did nothing.
      requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      // A check writes nothing, so there is nothing to refetch.
      if (mode !== 'preflight') qc.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => {
      setSummary(null);
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'The import could not be completed.');
    },
  });

  if (!open) {
    // Controlled: the caller renders the trigger, so nothing to show here.
    if (controlled) return null;
    return <ImportCsvButton onClick={() => setOpen(true)} />;
  }

  const hasFile = Boolean(csvText && csvText.trim() !== '');
  const checked = summary?.mode === 'preflight';
  const added = Boolean(summary && summary.mode !== 'preflight');
  const problems = summary ? summary.failed + summary.parse_errors.length : 0;
  const busy = importM.isPending;

  return (
    <div className="border border-ink-200 rounded-sm bg-ink-50/60 p-4 mb-3">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-sm font-medium text-ink-900">Import {label} from a spreadsheet</h4>
          <p className="text-xs text-ink-500 mt-0.5 max-w-2xl">
            Add many entries at once. Nothing is saved until you press
            <strong className="font-medium"> Add to catalog</strong>, and entries that came with
            the product are never changed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { clearFile(); setOpen(false); }}
          className="text-xs text-ink-500 hover:text-ink-900"
        >
          Close
        </button>
      </div>

      {/* ── Step 1 ─────────────────────────────────────────────────────── */}
      <div className="mb-4">
        <StepHeading n={1} done>Get the blank spreadsheet</StepHeading>
        <div className="pl-7">
          <a
            href={apiUrl(`/admin/threat-catalog/${entity}/import/template`)}
            className="inline-block text-sm px-3 py-1 border border-ink-300 rounded-sm text-ink-700 hover:bg-ink-50"
          >
            Download template
          </a>
          <p className="text-[11px] text-ink-500 mt-1">
            It has the right column headings and one filled-in example row. Delete the example,
            add your own rows, and save it as CSV.
          </p>
        </div>
      </div>

      {/* ── Step 2 ─────────────────────────────────────────────────────── */}
      <div className="mb-4">
        <StepHeading n={2} done={hasFile}>Choose your filled-in file</StepHeading>
        <div className="pl-7 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-sm px-3 py-1 border border-ink-300 rounded-sm text-ink-700 hover:bg-ink-50"
          >
            {hasFile ? 'Choose a different file' : 'Choose file'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={onPick}
          />
          {fileName ? (
            <span className="text-xs text-ink-700">
              {fileName}
              <button
                type="button"
                onClick={clearFile}
                className="ml-2 text-ink-500 hover:text-ink-900 underline"
              >
                remove
              </button>
            </span>
          ) : (
            <span className="text-[11px] text-ink-500">No file chosen yet.</span>
          )}
        </div>
      </div>

      {/* ── Step 3 ─────────────────────────────────────────────────────── */}
      <div>
        <StepHeading n={3} done={added}>Check it, then add it</StepHeading>
        <div className="pl-7">
          {!hasFile && (
            <p className="text-[11px] text-ink-500 mb-2">
              Choose a file above to continue.
            </p>
          )}

          <p className="text-[11px] text-ink-500 mb-2">
            Rows are matched on the <strong className="font-medium">Code</strong> column: anything
            already in the catalog is updated, anything new is added. Importing the same file twice
            is safe — it will not create duplicates. Entries that came with the product are left
            untouched and reported below.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!hasFile || busy}
              onClick={() => importM.mutate('preflight')}
              title={!hasFile ? 'Choose a file first' : undefined}
              className="text-sm px-3 py-1 border border-ink-300 rounded-sm text-ink-700 hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Working…' : 'Check for problems'}
            </button>
            <button
              type="button"
              disabled={!hasFile || busy}
              onClick={() => importM.mutate('upsert')}
              title={!hasFile ? 'Choose a file first' : undefined}
              className="text-sm px-3 py-1 bg-accent text-white border border-accent rounded-sm hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Working…' : 'Add to catalog'}
            </button>
            <span className="text-[11px] text-ink-500">
              Checking is optional — it tells you what would happen without saving anything.
            </span>
          </div>
        </div>
      </div>

      {/* ── Outcome ────────────────────────────────────────────────────── */}
      {error && (
        <div className="mt-3 text-xs text-risk-critical bg-red-50 border border-red-200 rounded-sm px-3 py-2">
          {error}
        </div>
      )}

      {summary && (
        <div ref={resultRef} className="mt-4 pt-3 border-t border-ink-200 text-xs space-y-2">
          {checked && problems === 0 && summary.total_rows > 0 && (
            <p className="text-ink-900">
              <strong>Looks good.</strong> {summary.total_rows} row
              {summary.total_rows === 1 ? '' : 's'} ready. Nothing has been saved yet — press
              <strong> Add to catalog</strong> when you are happy.
            </p>
          )}
          {checked && problems > 0 && (
            <p className="text-ink-900">
              <strong>
                {problems === 1 ? '1 row needs attention.' : `${problems} rows need attention.`}
              </strong>{' '}
              Nothing has been saved. Fix the rows listed below and choose the file again.
            </p>
          )}
          {added && (
            <div
              className={`rounded-sm px-3 py-2 border ${
                summary.failed > 0
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-green-50 border-green-200'
              }`}
            >
              <p className="text-sm font-medium text-ink-900">
                {summary.failed > 0 ? 'Imported with problems' : 'Import complete'}
              </p>
              <ul className="mt-1 space-y-0.5 text-ink-700">
                {summary.created > 0 && (
                  <li>Added {summary.created} new entr{summary.created === 1 ? 'y' : 'ies'}.</li>
                )}
                {summary.updated > 0 && (
                  <li>Updated {summary.updated} existing entr{summary.updated === 1 ? 'y' : 'ies'}.</li>
                )}
                {summary.skipped > 0 && (
                  <li>Left {summary.skipped} unchanged (they came with the product).</li>
                )}
                {summary.failed > 0 && (
                  <li className="text-risk-critical">
                    {summary.failed} row{summary.failed === 1 ? '' : 's'} could not be imported — see below.
                  </li>
                )}
                {summary.created === 0 && summary.updated === 0 && summary.failed === 0 && (
                  <li>Everything in the file already matched the catalog — no changes were needed.</li>
                )}
              </ul>
              <p className="text-[11px] text-ink-500 mt-1.5">
                The table below has been refreshed.
              </p>
            </div>
          )}
          {summary.total_rows === 0 && summary.parse_errors.length === 0 && (
            <p className="text-ink-700">
              That file has no rows in it. Check you saved the template after filling it in.
            </p>
          )}

          {summary.parse_errors.length > 0 && (
            <div className="text-risk-critical bg-red-50 border border-red-200 rounded-sm px-3 py-2">
              <p className="font-medium">
                {summary.parse_errors.length} line{summary.parse_errors.length === 1 ? '' : 's'} in the
                file could not be read at all, so {summary.parse_errors.length === 1 ? 'it was' : 'they were'}{' '}
                skipped. They are not counted above, so the file held more rows than were processed.
              </p>
              <div className="max-h-28 overflow-y-auto font-mono mt-1">
                {summary.parse_errors.slice(0, 20).map((e, i) => (
                  <div key={i}>Line {e.row_num}: {e.message}</div>
                ))}
              </div>
            </div>
          )}

          {summary.ignored_columns.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-ink-700">
              These columns were not recognised and were ignored:{' '}
              <span className="font-mono">{summary.ignored_columns.join(', ')}</span>. If that is a
              surprise, check you downloaded the template from this tab — several tabs share the
              first few columns.
            </div>
          )}

          {summary.results.some((r) => r.status === 'failed' || r.status === 'skipped') && (
            <div className="max-h-44 overflow-y-auto border border-ink-200 rounded-sm">
              <table className="w-full text-[11px]">
                <thead className="bg-ink-50 sticky top-0">
                  <tr className="text-left text-ink-500">
                    <th className="px-2 py-1">Row in your file</th>
                    <th className="px-2 py-1">Code</th>
                    <th className="px-2 py-1">What happened</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.results
                    .filter((r) => r.status === 'failed' || r.status === 'skipped')
                    .map((r) => (
                      <tr key={r.row_num} className="border-t border-ink-100">
                        <td className="px-2 py-1">{r.row_num}</td>
                        <td className="px-2 py-1 font-mono">{r.legacy_code ?? '—'}</td>
                        <td className={`px-2 py-1 ${r.status === 'failed' ? 'text-risk-critical' : 'text-ink-700'}`}>
                          {(r.errors ?? []).join('; ') || (r.status === 'skipped' ? 'Left unchanged.' : '')}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {added && (
            <button type="button" onClick={clearFile} className="text-accent hover:underline">
              Import another file
            </button>
          )}
        </div>
      )}
    </div>
  );
}
